import { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  CircularProgress,
  Select,
  MenuItem,
  IconButton,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardIos";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS } from "theme/tokens";

const PAGE_SIZES = [10, 50, 100];

export default function UserSecurityActivityPage() {
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1); // 1-based

  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);

  useEffect(() => {
    loadLanguageFile("UserSecurityActivityPage", "profile/UserSecurityActivityPage/UserSecurityActivityPage_lang");
  }, [language, loadLanguageFile]);

  const LANGUAGE = useMemo(
    () => componentLanguageData.UserSecurityActivityPage || { Main: {}, Error: {} },
    [componentLanguageData]
  );
  const L = LANGUAGE.Main || {};

  useEffect(() => {
    const fetchLoginHistory = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axios.get("/api/users/login-history", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLoginHistory(res.data || []);
      } catch (err) {
        setError(LANGUAGE.Error?.Content1 || "無法載入登入紀錄");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchLoginHistory();
  }, [LANGUAGE.Error]);

  // 切換 pageSize 時跳回第一頁
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  // 分頁計算
  const total = loginHistory.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);
  const pagedRows = loginHistory.slice((safePage - 1) * pageSize, safePage * pageSize);

  const summaryText = (L.PaginationSummary || "顯示 {from}～{to} 筆，共 {total} 筆資料")
    .replace("{from}", from)
    .replace("{to}", to)
    .replace("{total}", total);

  const formatTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${yyyy}.${mm}.${dd}  ${hh}:${mi}:${ss}`;
  };

  // EXE 登入會把整個機器資訊 JSON 寫進 device 欄位（mac/hostname/platform/cpu/disk）
  // 這裡解析出來只顯示「平台 · 主機名」，避免撐爆表格寬度
  const formatDevice = (raw) => {
    if (!raw) return "—";
    const trimmed = String(raw).trim();
    if (trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed);
        // platform: "Windows-11-10.0.26200-SP0" → 取前兩段「Windows 11」
        const platformShort = (parsed.platform || "")
          .split("-")
          .slice(0, 2)
          .join(" ");
        const host = parsed.hostname || "";
        if (platformShort && host) return `${platformShort} · ${host}`;
        if (platformShort) return platformShort;
        if (host) return host;
        return trimmed;
      } catch (e) {
        return trimmed;
      }
    }
    return trimmed;
  };

  return (
    <MDBox pt={2} pb={1}>
      <Card sx={{ p: 3, borderRadius: 3, boxShadow: 1 }}>
        {/* 標題列 + 顯示筆數選擇 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 4, height: 18, bgcolor: COLORS.main, borderRadius: 1 }} />
            <MDTypography variant="subtitle1" fontWeight="bold">
              {L.CardTitle || "查看您的登入記錄"}
            </MDTypography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" sx={{ color: COLORS.grayDark }}>
              {L.ShowLabel || "顯示"}
            </Typography>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              size="small"
              sx={{
                minWidth: 80,
                bgcolor: COLORS.grayLightest,
                borderRadius: 1.5,
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid",
                  borderColor: COLORS.main,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: 2,
                    mt: 0.5,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    "& .MuiMenuItem-root.Mui-selected": {
                      bgcolor: COLORS.lighter,
                      color: COLORS.main,
                      "&:hover": { bgcolor: COLORS.lighter },
                    },
                  },
                },
              }}
            >
              {PAGE_SIZES.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Table */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress sx={{ color: COLORS.main }} />
          </Box>
        ) : error ? (
          <MDTypography color="error" align="center" py={3}>
            {error}
          </MDTypography>
        ) : loginHistory.length === 0 ? (
          <Box py={6} textAlign="center">
            <Typography color="text.secondary">{L.EmptyText || "尚無登入紀錄"}</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table sx={{ tableLayout: "auto" }}>
                <TableBody>
                  {/* Header row — 單行不折 */}
                  <TableRow>
                    <TableCell sx={{ color: COLORS.grayDark, fontWeight: 500, fontSize: "0.85rem", py: 1.5, whiteSpace: "nowrap", width: 180 }}>
                      {L.Content1 || "時間"}
                    </TableCell>
                    <TableCell sx={{ color: COLORS.grayDark, fontWeight: 500, fontSize: "0.85rem", py: 1.5, whiteSpace: "nowrap", width: 140 }}>
                      {L.Content2 || "IP 位置"}
                    </TableCell>
                    <TableCell sx={{ color: COLORS.grayDark, fontWeight: 500, fontSize: "0.85rem", py: 1.5, whiteSpace: "nowrap", maxWidth: 320 }}>
                      {L.Content3 || "裝置"}
                    </TableCell>
                    <TableCell sx={{ color: COLORS.grayDark, fontWeight: 500, fontSize: "0.85rem", py: 1.5, whiteSpace: "nowrap", width: 100 }}>
                      {L.Content4 || "平台"}
                    </TableCell>
                    <TableCell sx={{ color: COLORS.grayDark, fontWeight: 500, fontSize: "0.85rem", py: 1.5, whiteSpace: "nowrap", width: 160 }}>
                      {L.Content5 || "動作"}
                    </TableCell>
                  </TableRow>

                  {/* Data rows — 全部白底，hover 才變淡橘（之前誤把 hover 寫成奇偶交錯）*/}
                  {pagedRows.map((item, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        bgcolor: COLORS.white,
                        "&:hover": { bgcolor: COLORS.lighter },
                      }}
                    >
                      <TableCell sx={{ fontSize: "0.85rem", py: 1.5, color: COLORS.black, whiteSpace: "nowrap" }}>
                        {formatTime(item.createdAt)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem", py: 1.5, color: COLORS.black, whiteSpace: "nowrap" }}>
                        {item.ipAddress || "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.85rem",
                          py: 1.5,
                          color: COLORS.black,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 320,
                        }}
                        title={item.device || ""}
                      >
                        {formatDevice(item.device)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem", py: 1.5, color: COLORS.black, whiteSpace: "nowrap" }}>
                        {item.platform || "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem", py: 1.5, color: COLORS.black, whiteSpace: "nowrap" }}>
                        {item.action || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Footer：總筆數 + 分頁箭頭 */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={2}
              pt={2}
            >
              <Typography variant="caption" sx={{ color: COLORS.main, fontWeight: 500 }}>
                {summaryText}
              </Typography>

              <Box display="flex" alignItems="center" gap={1}>
                <IconButton
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  sx={{
                    bgcolor: COLORS.grayLightest,
                    borderRadius: 1.5,
                    width: 32,
                    height: 32,
                    "&:hover": { bgcolor: COLORS.lighter },
                    "&.Mui-disabled": { bgcolor: COLORS.grayLightest, opacity: 0.5 },
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 14 }} />
                </IconButton>

                <Typography variant="caption" sx={{ minWidth: 24, textAlign: "center" }}>
                  {safePage}
                </Typography>

                <IconButton
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  sx={{
                    bgcolor: COLORS.grayLightest,
                    borderRadius: 1.5,
                    width: 32,
                    height: 32,
                    "&:hover": { bgcolor: COLORS.lighter },
                    "&.Mui-disabled": { bgcolor: COLORS.grayLightest, opacity: 0.5 },
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </Box>
          </>
        )}
      </Card>
    </MDBox>
  );
}
